import { useState } from 'react';

import { Flex, Grid, IconSparkle, Image, List, Spacer, Tag } from '@zillow/constellation';
import { Box, Button, Heading, Paragraph, Text } from '@zillow/constellation-10';

import ApplicationSampleModal from '../Modal/ApplicationSampleModal';

import { StyledAsteriskAfter } from '../../StyledComponents/StyledComponents';

import propertyUtils from '../../utils/propertyUtils';

import ApplicationsCSEvents from '../../model/ClickstreamEvent/ApplicationsCSEvents';

import ApplicationDemo from '../../images/applicationDemo.png';

import { useSelector } from '../../selectors/useSelector';
import { AppSelectors } from '../../selectors';

type Props = {
  children: React.ReactNode;
  isIntroModal?: boolean;
};

const ApplicationsWrapper = ({ children, isIntroModal = false }: Props) => {
  const makeIsScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const {
    properties: { currentProperty },
    user,
  } = useSelector((state) => state);

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const [displaySampleModal, setDisplaySampleModal] = useState(false);

  let intro = "You'll automatically receive these benefits:";
  if (isIntroModal) {
    intro = 'Enable Zillow applications so renters can apply directly from your listing. ' + intro;
  }

  return (
    <div data-testid="ApplicationsWrapper">
      <Spacer marginBottom="sm">
        <Tag appearance="info" marginRight="xs">
          Free
        </Tag>
      </Spacer>
      <Heading level={4}>
        {isIntroModal ? (
          'Receive Zillow applications'
        ) : (
          <StyledAsteriskAfter>Receive Zillow applications for this listing?</StyledAsteriskAfter>
        )}
      </Heading>
      <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="md">
        <Grid gridColumnStart="1" gridColumnEnd={!makeIsScreenDesktop ? 'span 12' : 'span 8'}>
          <div data-test="ApplicationsContent">
            <Paragraph css={{ marginTop: '600' }}>{intro}</Paragraph>
            <List>
              <List.Item paddingY={0}>Credit report from identity verified applicants</List.Item>
              <List.Item paddingY={0}>Background check (includes eviction &amp; criminal record)</List.Item>
              <List.Item paddingY={0}>Income &amp; employment history</List.Item>
              <List.Item paddingY={0}>Residence history &amp; previous landlord contacts</List.Item>
            </List>
            {!isRoomForRent && (
              <Box css={{ marginTop: '600', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Spacer paddingRight="xs">
                  <IconSparkle fontColor="brand" size="sm" />
                </Spacer>
                <Text textStyle="body-sm">
                  Your listing gets an additional promotion when you choose to receive Zillow applications
                </Text>
              </Box>
            )}
          </div>
        </Grid>

        <Grid
          display="grid"
          gridColumnStart={!makeIsScreenDesktop ? '1' : '9'}
          gridColumnEnd={!makeIsScreenDesktop ? 'span 12' : 'span 4'}
          alignItems="center"
          justifyContent="center"
        >
          <Flex display="flex" flexDirection="column" justifyContent="center">
            <Image alt="sample application icon" src={ApplicationDemo} />

            <Button
              tone="brand"
              emphasis="filled"
              onClick={() => {
                setDisplaySampleModal(true);
                ApplicationsCSEvents.ClickSampleApplicationLinkInModal({ currentProperty, user });
              }}
              data-eventid={ApplicationsCSEvents.ClickSampleApplicationLinkInModal.data_event_id}
            >
              View sample
            </Button>
          </Flex>
        </Grid>

        <Grid gridColumnStart="1" gridColumnEnd="span 12">
          {children}
        </Grid>
      </Grid>

      {displaySampleModal && <ApplicationSampleModal onClose={() => setDisplaySampleModal(false)} />}
    </div>
  );
};

export default ApplicationsWrapper;
